import { useEffect, useState } from 'react';
import {
  Box,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Button,
  AppBar,
  Menu,
  MenuItem,
  List,
  IconButton,
  SvgIcon,
  Tooltip,
  Divider,
  useTheme,
  SvgIconOwnProps,
} from '@mui/material';

import DashboardIcon from '@mui/icons-material/SpaceDashboardTwoTone';
import JobsIcon from '@mui/icons-material/WorkTwoTone';
import EmailIcon from '@mui/icons-material/EmailTwoTone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContractIcon from '@mui/icons-material/DescriptionTwoTone';
import AnalyticIcon from '@mui/icons-material/AssessmentTwoTone';
import Link from 'components/common/Link';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from './SessionContext';
import { HARBOR_DEMO_ACCOUNT_ID } from 'lib/harborDemoAccount';
import { useTheme as useThemeContext } from '../lib/ThemeContext';
import { getApiUrl, readFile } from '../lib/api/public';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleIcon from '@mui/icons-material/SupervisedUserCircleTwoTone';
import SettingsIcon from '@mui/icons-material/SettingsTwoTone';
import BusinessIcon from '@mui/icons-material/HandshakeTwoTone';
import CalendarIcon from '@mui/icons-material/TodayTwoTone';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedInTwoTone';
import SequenceIcon from '@mui/icons-material/MoveDownTwoTone';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QuickAdd from './QuickAdd';
import GlobalSearch from './GlobalSearch';
import Notifications from './Notifications';
import { User } from './types';
import AccountStatusAlerts from './common/AccountStatusAlerts';
import WorkspaceMenu from './WorkspaceMenu';
import ResetPasswordForm from './user/ResetPasswordForm';
import GoogleSignupDetailsForm from './user/GoogleSignupDetailsForm';
import VersionChecker from './VersionChecker';
import ConnectionAlert from './integrations/ConnectionAlert';
import Loader from './common/Loader';
import ResponsiveDrawer from './common/ResponsiveDrawer';
import OnboardingRobot from 'components/onboarding/OnboardingRobot';
import ExtensionChecker from './ExtensionChecker';
import { EXPANDED_SIDEBAR_WIDTH_PX } from './settings/settingsHomeNavItems';
import {
  getNavigationTreeLineColor,
  getNavigationDividerColor,
  formatAccountRoleLabel,
  getSubdomain,
  formatUserDisplayName,
} from './utils';

const API_URL = getApiUrl();

const SidebarCollapseIcon = ({ collapsed }: { collapsed: boolean }) => (
  <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 16, fill: 'none' }}>
    <path
      d="M4 6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2l0 -12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d={collapsed ? 'M15 4v16' : 'M9 4v16'}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d={collapsed ? 'M9 10l2 2l-2 2' : 'M15 10l-2 2l2 2'}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SvgIcon>
);

export type BreadCrumb = {
  parent?: {
    link: string;
    title: React.ReactNode;
    titleText?: string;
  };
  subParent?: {
    link: string;
    title: React.ReactNode;
    titleText?: string;
  };
  title: React.ReactNode;
  titleText?: string;
};

const AuthLayoutContent = ({
  children,
  isLoading,
  user,
  drawerCollapsed,
  breadCrumb,
  isTrialExpired,
}: {
  children: React.ReactNode;
  isLoading?: boolean;
  user: User | null;
  drawerCollapsed: boolean;
  breadCrumb?: BreadCrumb;
  isTrialExpired?: boolean;
}) => {
  const router = useRouter();
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const renderIcon = () => {
    if (router.pathname.includes('settings')) {
      return <SettingsIcon />;
    }

    if (router.pathname === '/') {
      return <DashboardIcon />;
    }

    if (router.pathname.includes('job')) {
      return <JobsIcon />;
    }

    if (router.pathname.includes('task')) {
      return <AssignmentTurnedInIcon />;
    }

    if (router.pathname.includes('candidate')) {
      return <PeopleIcon />;
    }

    if (
      router.pathname.includes('clients') ||
      router.pathname.includes('/client') ||
      router.pathname === '/contacts' ||
      router.pathname === '/prospects'
    ) {
      return <BusinessIcon />;
    }

    if (router.pathname.includes('analytics')) {
      return <AnalyticIcon />;
    }

    if (router.pathname.includes('calendars')) {
      return <CalendarIcon />;
    }

    if (router.pathname.includes('account')) {
      return <AccountCircleIcon />;
    }

    if (router.pathname.includes('sequence')) {
      return <SequenceIcon />;
    }

    if (router.pathname.includes('duplicate-checker')) {
      return <ContentCopyIcon />;
    }

    if (router.pathname.includes('mass-import')) {
      return <ContentCopyIcon />;
    }

    if (router.pathname.includes('email')) {
      return <EmailIcon />;
    }

    if (router.pathname.includes('contracts')) {
      return <ContractIcon />;
    }

    return null;
  };

  const renderBreadCrumb = () => {
    if (!breadCrumb) {
      return null;
    }

    const containerStyle = {
      gap: '4px',
      alignItems: 'center',
      minWidth: 0,
      fontSize: { xs: '12px', sm: '12px', md: '13px' },
      display: 'flex',
    };

    const linkStyle = {
      textDecoration: 'none',
      color: theme.palette.text.secondary,
      ':hover': {
        color: theme.palette.text.primary,
      },
      ':visited': {
        color: theme.palette.text.secondary,
      },
      transition: 'all 0.2s ease',
      display: { xs: 'none', md: 'flex' },
    };

    const separatorStyle = {
      margin: '0 3px',
      color: theme.palette.text.disabled,
      fontSize: '0.75rem',
    };

    const titleStyle = {
      color: theme.palette.text.primary,
    };

    return (
      <Box sx={containerStyle}>
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>{renderIcon()}</Box>

        {breadCrumb.parent && (
          <>
            <Link href={breadCrumb.parent.link} sx={linkStyle}>
              {breadCrumb.parent.title}
            </Link>
            <Box component="span" sx={{ ...separatorStyle, display: { xs: 'none', md: 'inherit' } }}>
              ›
            </Box>
          </>
        )}

        {breadCrumb.subParent && (
          <>
            <Link href={breadCrumb.subParent.link} sx={linkStyle}>
              {breadCrumb.subParent.title}
            </Link>
            <Box component="span" sx={{ ...separatorStyle, display: { xs: 'none', md: 'inherit' } }}>
              ›
            </Box>
          </>
        )}

        <Box
          component="span"
          sx={{ ...titleStyle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {breadCrumb.title}
        </Box>

        <AccountStatusAlerts user={user} isTrialExpired={isTrialExpired} />
      </Box>
    );
  };

  const userRole = formatAccountRoleLabel(user?.role);
  const userDisplayName = user ? formatUserDisplayName(user) : '';
  const userInitial = user?.firstName?.[0] || user?.email?.[0] || '';
  const agencyName = user?.account?.name || '';

  let userInfo = null;

  if (user) {
    userInfo = (
      <span>
        {userRole}: {userDisplayName}
      </span>
    );
  }

  const renderProfileAvatar = (size = 22) => (
    <Avatar
      src={user?.avatarUrl ? readFile(user.avatarUrl) : undefined}
      alt=""
      sx={theme => ({
        width: size,
        height: size,
        fontSize: size <= 22 ? '0.7rem' : '0.875rem',
        fontWeight: 600,
        bgcolor: user?.avatarUrl
          ? 'transparent'
          : theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.16)'
            : 'secondary.main',
        color: user?.avatarUrl ? undefined : 'secondary.contrastText',
      })}
    >
      {userInitial.toUpperCase()}
    </Avatar>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <AppBar
        color="secondary"
        className="app-bar"
        position="static"
        elevation={0}
        sx={theme => ({
          color: theme.palette.text.primary,
          backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          padding: '9px 20px',
          alignItems: 'center',
          flexShrink: 0,
          width: {
            xs: '100%',
            md: drawerCollapsed ? 'calc(100% - 44px)' : `calc(100% - ${EXPANDED_SIDEBAR_WIDTH_PX}px)`,
          },
          marginLeft: {
            xs: 0,
            md: drawerCollapsed ? '44px' : `${EXPANDED_SIDEBAR_WIDTH_PX}px`,
          },
          zIndex: 1100,
          borderRadius: 0,
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <ResponsiveDrawer.Toggler />
          {renderBreadCrumb()}
        </Box>

        <ConnectionAlert />

        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QuickAdd />
            <GlobalSearch />

            <Notifications />

            <Button
              color="secondary"
              id="account-menu-button"
              aria-controls={anchorEl ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
              aria-label="Account menu"
              variant="outlined"
              size="small"
              onClick={handleMenuClick}
              className="go-to-profile"
              endIcon={anchorEl ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              sx={theme => ({
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
                border: 'none',
                px: 1,
                minWidth: 0,
                color: 'text.primary',
                gap: 0.75,
                '& .MuiButton-endIcon': {
                  ml: { xs: 0, md: 0.5 },
                  mr: 0,
                },
              })}
            >
              {renderProfileAvatar(22)}
              <Box
                component="span"
                sx={{
                  display: { xs: 'none', md: 'inline' },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 160,
                }}
              >
                {userInfo}
              </Box>
            </Button>
          </Box>

          <Menu
            id="account-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            slotProps={{
              list: {
                'aria-labelledby': 'account-menu-button',
                dense: true,
                sx: { py: 0.5 },
              },
              paper: {
                sx: {
                  mt: 0.75,
                  minWidth: 220,
                  maxWidth: 280,
                  overflow: 'hidden',
                  border: theme => `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                },
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.5,
                py: 1.25,
              }}
            >
              {renderProfileAvatar(36)}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box
                  component="div"
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: 'text.primary',
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {userDisplayName || 'Account'}
                </Box>
                {userRole ? (
                  <Box
                    component="div"
                    sx={{
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {userRole}
                  </Box>
                ) : null}
                {agencyName ? (
                  <Box
                    component="div"
                    sx={{
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {agencyName}
                  </Box>
                ) : null}
              </Box>
            </Box>

            <Divider />

            <MenuItem
              component={NextLink}
              href="/settings/account"
              onClick={handleMenuClose}
              sx={{ gap: 1, py: 0.75, mx: 0.5, borderRadius: 1 }}
            >
              <ListItemIcon sx={{ minWidth: '28px !important', color: 'text.secondary' }}>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Account settings" primaryTypographyProps={{ fontSize: '0.8125rem' }} />
            </MenuItem>

            <MenuItem
              component="a"
              href={`${API_URL}/auth/logout`}
              onClick={handleMenuClose}
              sx={{ gap: 1, py: 0.75, mx: 0.5, mb: 0.5, borderRadius: 1 }}
            >
              <ListItemIcon sx={{ minWidth: '28px !important', color: 'text.secondary' }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Log out" primaryTypographyProps={{ fontSize: '0.8125rem' }} />
            </MenuItem>
          </Menu>
        </Box>
      </AppBar>

      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
          }}
        >
          <Loader />
        </Box>
      )}

      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

type Props = {
  children: React.ReactNode;
  sideBar?: React.ReactNode;
  isLoading?: boolean;
  breadCrumb?: BreadCrumb;
  testId?: string;
};

type SubMenuItem = {
  text: string;
  link: string;
  activeCheck: () => boolean;
  testId?: string;
};

type RenderMenuItemProps = {
  text: string;
  link: string;
  Icon: React.ComponentType<{ sx?: React.CSSProperties } & SvgIconOwnProps>;
  activeCheck: () => boolean;
  Indicator?: React.ComponentType<{ sx?: React.CSSProperties }>;
  style?: React.CSSProperties;
  drawerCollapsed?: boolean;
  testId?: string;
  subItems?: SubMenuItem[];
  suppressActive?: boolean;
};

const renderMenuItem = ({
  text,
  link,
  Icon,
  activeCheck,
  Indicator,
  style,
  drawerCollapsed,
  testId,
  subItems,
  suppressActive,
}: RenderMenuItemProps) => {
  if (link.includes('clients') && !link.includes('/contacts')) {
    const view = localStorage.getItem('ats-clients-view') || '';
    link = `${link}?view=${view}`;
  }

  const withClientsTableView = (href: string) => {
    if (href !== '/clients') {
      return href;
    }
    const view = typeof localStorage !== 'undefined' ? localStorage.getItem('ats-clients-view') || '' : '';
    return view ? `/clients?view=${view}` : '/clients';
  };

  const hasActiveSubItem = subItems?.some(sub => sub.activeCheck()) ?? false;

  const parentItemWithSubmenu =
    subItems?.length && !drawerCollapsed ? (
      <Box>
        <ListItem
          data-testid={testId}
          sx={theme => ({
            mx: 1,
            mt: 0.5,
            py: 0,
            borderRadius: '4px',
            transition: 'background-color 0.3s ease',
            width: 'auto',
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
            ...(hasActiveSubItem
              ? {
                  backgroundColor: theme.palette.primary.main,
                }
              : {}),
            ...(style || {}),
          })}
          disablePadding
        >
          <Link
            href={link}
            underline="none"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              textDecoration: 'none',
              color: 'inherit',
              padding: '5px 8px',
              '&:hover': { textDecoration: 'none' },
            }}
          >
            <ListItemIcon
              sx={{
                color: 'secondary.contrastText',
                minWidth: '22px',
                opacity: 0.6,
              }}
            >
              <Icon />
            </ListItemIcon>
            <ListItemText
              sx={{
                color: '#e7e0e0',
                margin: 0,
                '& .MuiListItemText-primary': { fontSize: '13px', fontWeight: 600 },
              }}
            >
              {text}
            </ListItemText>
          </Link>
        </ListItem>
        <Box
          sx={theme => ({
            // Match parent ListItem horizontal inset, then pad like StackTabs so the tree + rows sit inside the column.
            mx: 1,
            pl: `calc(${theme.spacing(1.5)} + 5px)`,
            pr: theme.spacing(1),
            boxSizing: 'border-box',
            mt: 0.75,
            mb: 0,
            pb: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            overflowX: 'hidden',
            overflowY: 'visible',
          })}
        >
          {subItems.map((sub, idx) => {
            const linkSub = sub;
            const isLast = idx === subItems.length - 1;

            return (
              <Link
                key={linkSub.testId || linkSub.link}
                href={withClientsTableView(linkSub.link)}
                style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                data-testid={linkSub.testId}
              >
                <Box sx={{ position: 'relative', pl: 3 }}>
                  {/* Continuous stem under non-last items (covers row gap). */}
                  {!isLast && (
                    <Box
                      sx={theme => ({
                        position: 'absolute',
                        left: 8,
                        top: 0,
                        bottom: '-4px',
                        width: 0,
                        borderLeft: `1px solid ${getNavigationTreeLineColor(theme, true)}`,
                        pointerEvents: 'none',
                      })}
                    />
                  )}
                  {/* Rounded elbow into the row, Facebook-comment style. */}
                  <Box
                    sx={theme => ({
                      position: 'absolute',
                      left: 8,
                      top: 0,
                      width: 16,
                      height: '50%',
                      borderLeft: `1px solid ${getNavigationTreeLineColor(theme, true)}`,
                      borderBottom: `1px solid ${getNavigationTreeLineColor(theme, true)}`,
                      borderBottomLeftRadius: '12px',
                      pointerEvents: 'none',
                    })}
                  />
                  <ListItem
                    dense
                    sx={theme => ({
                      padding: '4px 8px',
                      borderRadius: '4px',
                      minHeight: 26,
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                      },
                      ...(linkSub.activeCheck()
                        ? {
                            backgroundColor: theme.palette.primary.main,
                            '& .MuiListItemText-primary': {
                              color: theme.palette.primary.contrastText,
                            },
                          }
                        : {}),
                    })}
                  >
                    <ListItemText
                      sx={theme => ({
                        color: theme.palette.secondary.contrastText,
                        margin: 0,
                        opacity: 0.8,
                        '& .MuiListItemText-primary': { fontSize: '12px', lineHeight: 1.3 },
                      })}
                    >
                      {linkSub.text}
                    </ListItemText>
                  </ListItem>
                </Box>
              </Link>
            );
          })}
        </Box>
      </Box>
    ) : null;

  if (parentItemWithSubmenu) {
    return parentItemWithSubmenu;
  }

  const parentItem = (
    <Link
      href={link}
      style={{
        padding: '2px 6px',
        textDecoration: 'none',
        color: 'inherit',
      }}
      data-testid={testId}
    >
      <ListItem
        sx={theme => ({
          padding: drawerCollapsed ? '8px' : '5px 8px',
          borderRadius: '4px',
          transition: 'background-color 0.3s ease',
          ':hover': {
            backgroundColor: theme.palette.primary.main,
          },
          ...(!suppressActive && activeCheck()
            ? {
                backgroundColor: theme.palette.primary.main,
              }
            : {}),
          ...(style || {}),
        })}
      >
        <ListItemIcon
          sx={{
            color: 'secondary.contrastText',
            minWidth: '22px',
            opacity: drawerCollapsed ? 0.9 : 0.6,
          }}
        >
          {drawerCollapsed ? (
            <Tooltip title={text} placement="right">
              <Icon />
            </Tooltip>
          ) : (
            <Icon />
          )}
        </ListItemIcon>

        {!drawerCollapsed && (
          <ListItemText
            sx={{
              color: '#e7e0e0',
              margin: 0,
              '& .MuiListItemText-primary': { fontSize: '13px', fontWeight: 600 },
            }}
          >
            {text}
          </ListItemText>
        )}

        {Indicator && <Indicator />}
      </ListItem>
    </Link>
  );

  return parentItem;
};

const AuthLayout = ({ children, isLoading, breadCrumb, testId }: Props) => {
  const {
    user,
    drawerCollapsed,
    setDrawerCollapsed,
    renderSubscriptionInfo,
    shouldHideInfoSections,
    isTrialExpired,
    fetchUser,
  } = useSession();

  const router = useRouter();
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showGoogleSignupDetails, setShowGoogleSignupDetails] = useState(false);

  useEffect(() => {
    if (user?.isResetPasswordRequired) {
      setShowResetPassword(true);
    }
  }, [user?.isResetPasswordRequired]);

  useEffect(() => {
    setShowGoogleSignupDetails(Boolean(user?.needsGoogleSignupDetails));
  }, [user?.needsGoogleSignupDetails]);

  const pn = router.pathname;
  const asPath = router.asPath || pn;

  const suppressSiblingActive = false;

  const showInvoicesInNav = user?.role === 'superAdmin';
  const showAccountOwnersInNav = Boolean(user?.isSystemAdmin);
  const showDemoCallsInNav = String(user?.accountId || '') === HARBOR_DEMO_ACCOUNT_ID;

  return (
    <ResponsiveDrawer id="auth-layout" testId={testId}>
      <AuthLayoutContent
        isLoading={isLoading}
        user={user}
        breadCrumb={breadCrumb}
        drawerCollapsed={drawerCollapsed}
        isTrialExpired={isTrialExpired}
      >
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <ResponsiveDrawer.Content>
            <Box
              sx={{
                display: 'flex',
                flexDirection: drawerCollapsed ? 'column' : 'row',
                justifyContent: drawerCollapsed ? 'flex-start' : 'space-between',
                alignItems: 'center',
                marginBottom: '4px',
              }}
            >
              <WorkspaceMenu user={user} drawerCollapsed={drawerCollapsed} />
              <IconButton
                onClick={() => setDrawerCollapsed(!drawerCollapsed)}
                aria-label={drawerCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                sx={{
                  color: 'text.secondary',
                  width: 24,
                  height: 24,
                  margin: '8px 4px',
                  padding: 0,
                  alignSelf: 'center',
                  backgroundColor: 'action.hover',
                  '&:hover': {
                    color: 'secondary.contrastText',
                    backgroundColor: 'action.hover',
                  },
                  display: { xs: 'none', md: 'inherit' },
                }}
                size="small"
              >
                <SidebarCollapseIcon collapsed={drawerCollapsed} />
              </IconButton>
            </Box>

            <Box
              sx={{
                flex: '1 1 auto',
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(255, 255, 255, 0.25) transparent',
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255, 255, 255, 0.25)', borderRadius: 2 },
              }}
            >
              <List
                data-testid="@sidebar"
                sx={{
                  paddingTop: '0px',
                  display: 'flex',
                  minHeight: '100%',
                  flexDirection: 'column',
                  paddingBottom: 2,
                }}
              >
                {renderMenuItem({
                  text: 'Overview',
                  link: '/',
                  Icon: DashboardIcon,
                  activeCheck: () => pn === '/',
                  drawerCollapsed,
                  testId: '@sidebar-overview',
                  suppressActive: suppressSiblingActive,
                })}
                <Divider sx={theme => ({ borderColor: getNavigationDividerColor(theme, true), marginY: 0.75 })} />
                {renderMenuItem({
                  text: 'Calendars',
                  link: '/calendars',
                  Icon: CalendarIcon,
                  activeCheck: () => !pn.includes('/settings') && pn.includes('/calendars'),
                  drawerCollapsed,
                  testId: '@sidebar-calendars',
                  suppressActive: suppressSiblingActive,
                })}
                {renderMenuItem({
                  text: 'Tasks',
                  link: '/tasks',
                  Icon: AssignmentTurnedInIcon,
                  activeCheck: () => !pn.includes('/settings') && pn.includes('/task'),
                  drawerCollapsed,
                  testId: '@sidebar-tasks',
                  suppressActive: suppressSiblingActive,
                })}
                {renderMenuItem({
                  text: 'Candidates',
                  link: '/candidates',
                  Icon: PeopleIcon,
                  activeCheck: () =>
                    !pn.includes('/settings') &&
                    (pn.includes('/candidates') || pn.includes('/candidate') || pn.includes('/duplicate-checker')),
                  drawerCollapsed,
                  testId: '@sidebar-candidates',
                  suppressActive: suppressSiblingActive,
                  subItems: [
                    {
                      text: 'Candidates',
                      link: '/candidates',
                      activeCheck: () =>
                        !pn.includes('/settings') && (pn.includes('/candidates') || pn.includes('/candidate')),
                      testId: '@sidebar-candidates-list',
                    },
                    {
                      text: 'Duplicates',
                      link: '/duplicate-checker',
                      activeCheck: () => !pn.includes('/settings') && pn.includes('/duplicate-checker'),
                      testId: '@sidebar-candidates-duplicates',
                    },
                  ],
                })}
                {renderMenuItem({
                  text: 'Jobs',
                  link: '/jobs',
                  Icon: JobsIcon,
                  activeCheck: () =>
                    !pn.includes('/settings') &&
                    (pn.includes('/jobs') || pn.includes('/job') || pn.includes('/analytics')),
                  testId: '@sidebar-jobs',
                  drawerCollapsed,
                  suppressActive: suppressSiblingActive,
                  subItems: [
                    {
                      text: 'Jobs',
                      link: '/jobs',
                      activeCheck: () => !pn.includes('/settings') && (pn.includes('/jobs') || pn.includes('/job')),
                      testId: '@sidebar-jobs-list',
                    },
                    {
                      text: 'Analytics',
                      link: '/analytics',
                      activeCheck: () => !pn.includes('/settings') && pn.includes('/analytics'),
                      testId: '@sidebar-jobs-analytics',
                    },
                  ],
                })}
                {renderMenuItem({
                  text: 'Clients',
                  link: '/clients',
                  Icon: BusinessIcon,
                  activeCheck: () =>
                    !pn.includes('/settings') &&
                    (pn === '/clients' ||
                      pn === '/clients/invoices' ||
                      pn === '/accounts' ||
                      pn === '/demo-calls' ||
                      pn === '/demo-call' ||
                      pn === '/contacts' ||
                      pn === '/prospects' ||
                      pn.includes('/client')),
                  drawerCollapsed,
                  testId: '@sidebar-clients',
                  suppressActive: suppressSiblingActive,
                  subItems: [
                    {
                      text: 'Clients',
                      link: '/clients',
                      activeCheck: () => pn === '/clients',
                      testId: '@sidebar-clients-list',
                    },
                    {
                      text: 'Prospects',
                      link: '/prospects',
                      activeCheck: () => pn === '/prospects',
                      testId: '@sidebar-clients-prospects',
                    },
                    {
                      text: 'Contacts',
                      link: '/contacts',
                      activeCheck: () => pn === '/contacts',
                      testId: '@sidebar-clients-contacts',
                    },
                    ...(showInvoicesInNav
                      ? [
                          {
                            text: 'Invoices',
                            link: '/clients/invoices',
                            activeCheck: () => pn === '/clients/invoices',
                            testId: '@sidebar-clients-invoices',
                          },
                        ]
                      : []),
                    ...(showDemoCallsInNav
                      ? [
                          {
                            text: 'Demo Calls',
                            link: '/demo-calls',
                            activeCheck: () => pn === '/demo-calls' || pn === '/demo-call',
                            testId: '@sidebar-clients-demo-calls',
                          },
                        ]
                      : []),
                    ...(showAccountOwnersInNav
                      ? [
                          {
                            text: 'Accounts',
                            link: '/accounts',
                            activeCheck: () => pn === '/accounts',
                            testId: '@sidebar-clients-accounts',
                          },
                        ]
                      : []),
                  ],
                })}
                <Divider sx={theme => ({ borderColor: getNavigationDividerColor(theme, true), marginY: 0.75 })} />
                {renderMenuItem({
                  text: 'Outreach',
                  link: '/sequences',
                  Icon: SequenceIcon,
                  activeCheck: () =>
                    (!pn.includes('/settings') && pn.includes('/sequences')) ||
                    pn.includes('/sequences/templates') ||
                    pn.includes('/send-email') ||
                    pn.includes('/sequences/send-email') ||
                    pn.includes('/email-templates') ||
                    pn.includes('/sender-accounts'),
                  drawerCollapsed,
                  testId: '@sidebar-sequences',
                  suppressActive: suppressSiblingActive,
                  subItems: [
                    {
                      text: 'Sequences',
                      link: '/sequences',
                      activeCheck: () => pn === '/sequences',
                      testId: '@sidebar-sequences-all',
                    },
                    {
                      text: 'Send Email',
                      link: '/sequences/send-email',
                      activeCheck: () => pn === '/sequences/send-email',
                      testId: '@sidebar-sequences-send-email',
                    },
                    {
                      text: 'Sequence Templates',
                      link: '/sequences/templates',
                      activeCheck: () => pn.includes('/sequences/templates'),
                      testId: '@sidebar-sequences-templates',
                    },
                    {
                      text: 'Email Templates',
                      link: '/sequences/email-templates',
                      activeCheck: () => pn.includes('/email-templates'),
                      testId: '@sidebar-email-templates',
                    },
                  ],
                })}
                {renderMenuItem({
                  text: 'Contracts',
                  link: '/contracts',
                  Icon: ContractIcon,
                  activeCheck: () => !pn.includes('/settings') && pn.includes('/contracts'),
                  drawerCollapsed,
                  testId: '@sidebar-contracts',
                  suppressActive: suppressSiblingActive,
                })}
                <Box sx={{ flexGrow: 1, minHeight: 0 }} />
                {renderMenuItem({
                  text: 'Settings',
                  link: `/settings/account?returnTo=${encodeURIComponent(asPath)}`,
                  Icon: SettingsIcon,
                  activeCheck: () => pn.includes('/settings'),
                  drawerCollapsed,
                  testId: '@sidebar-settings',
                  suppressActive: suppressSiblingActive,
                })}
              </List>
            </Box>

            {!drawerCollapsed && (
              <Divider sx={theme => ({ borderColor: getNavigationDividerColor(theme, true), marginBottom: 1 })} />
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {renderSubscriptionInfo ? renderSubscriptionInfo() : null}
            </Box>

            {!shouldHideInfoSections() && (
              <Box sx={{ display: drawerCollapsed ? 'none' : { xs: 'none', md: 'block' } }}>
                <VersionChecker />
                <ExtensionChecker />
              </Box>
            )}
          </ResponsiveDrawer.Content>
          <Box
            sx={{
              marginLeft: { xs: '0', md: drawerCollapsed ? '60px' : `${EXPANDED_SIDEBAR_WIDTH_PX + 20}px` },
              marginRight: { xs: 0, md: '10px' },
              pt: 2,
              pb: 2,
              paddingRight: { xs: '16px', md: '8px' },
              paddingLeft: { xs: '16px', md: '0' },
              width: {
                xs: '100%',
                md: drawerCollapsed ? 'calc(100% - 72px)' : `calc(100% - ${EXPANDED_SIDEBAR_WIDTH_PX + 30}px)`,
              },
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              transition: 'margin-left 0.3s ease, width 0.3s ease',
            }}
          >
            {children}
            {showResetPassword && <ResetPasswordForm onSuccess={() => setShowResetPassword(false)} />}
            {showGoogleSignupDetails && (
              <GoogleSignupDetailsForm
                onSuccess={async () => {
                  setShowGoogleSignupDetails(false);
                  await fetchUser();
                }}
              />
            )}
            {!shouldHideInfoSections() && <OnboardingRobot />}
          </Box>
        </Box>
      </AuthLayoutContent>
    </ResponsiveDrawer>
  );
};

export default AuthLayout;

export const LoginRequired = ({ children }: Props) => {
  const { user, loadingUser, redirectToLogin } = useSession();
  const { setTheme } = useThemeContext();

  const router = useRouter();

  useEffect(() => {
    if (redirectToLogin) {
      void router.push(`/login?redirect=${encodeURIComponent(router.asPath)}`);
    }

    if (user) {
      if (!localStorage.getItem('theme-preference')) {
        setTheme(user.theme || 'light');
      }

      const account = user.account;
      const href = window.location.href;
      const currentSubdomain = getSubdomain();

      if (account?.subdomain && account?.isSubdomainReady) {
        if (currentSubdomain !== account.subdomain) {
          window.location.href = href.replace(`${currentSubdomain}.`, `${account.subdomain}.`);
        }
      }
    }
  }, [user, router, loadingUser, redirectToLogin, setTheme]);

  if (!user) return null;

  return <>{children}</>;
};
